import React, { useCallback, useEffect, useRef, useState } from "react";
import { makeStyles } from "tss-react/mui";
// import { withPixelLineHeight } from '../../../base/styles/functions.web';
import Button from "../../../base/ui/components/web/Button";
import Input from "../../../base/ui/components/web/Input";
import RadioButton from "../../../base/ui/components/web/RadioButton";
import { BUTTON_TYPES } from "../../../base/ui/constants.web";
import { ANSWERS_LIMIT, CHAR_LIMIT, POLL_TYPE } from "../../constants";
import AbstractPollCreate from "../AbstractPollCreate";
import { Icon, IconCancelSelection } from "../../../base/icons";
import { useSelector } from "react-redux";
import { getBrandingColorPalette } from "../../../base/styles";
const PollCreate = ({ addAnswer, answers, isSubmitDisabled, onSubmit, pollType, question, removeAnswer, setAnswer, setCreateMode, setPollType, setQuestion, t, }) => {
    var _a;
    /*
     * This ref stores the Array of answer input fields, allowing us to focus on them.
     * This array is maintained by registerfieldRef and the useEffect below.
     */
    const answerInputs = useRef([]);
    const interfaceConfig = useSelector((state => state['features/base/config'].interfaceConfig));
    const useStyles = makeStyles()((theme) => {
        var _a;
        return {
            container: {
                position: "relative",
                background: '#FFFFFF',
                display: 'flex',
                flexDirection: 'column',
                maxHeight: "calc(100vh - 100px)", // Dynamic height based on viewport
                minHeight: "100%", // Minimum height for usability
                padding: '20px 10px',
                borderRadius: '0px 0px 20px 20px',
                overflow: 'hidden' // Prevent container overflow
            },
            createContainer: {
                padding: "0 24px",
                overflowY: "auto",
                flex: 1,
                minHeight: 0, // Important for flex child to be scrollable
                display: 'flex',
                flexDirection: 'column',
                overflowX: 'hidden',
                scrollbarWidth: 'thin'
            },
            header: {
                // ...withPixelLineHeight(theme.typography.heading6),
                color: "#fff",
                fontSize: "20px",
                lineHeight: "28px",
                margin: "20px 0px",
                fontWeight: 600,
                display: 'none'
            },
            questionContainer: {
                paddingBottom: "24px",
                flexShrink: 0, // Don't shrink the question area
                // borderBottom: `1px solid #3D3D3D`,
            },
            answerList: {
                listStyleType: "none",
                margin: 0,
                padding: 0,
                flex: 1,
                overflowY: "auto",
                minHeight: 0, // Allow shrinking for scroll
                paddingBottom: "10px", // Add some bottom padding for better UX
                scrollbarWidth: 'none'
            },
            answer: {
                marginBottom: "10px",
                background: "#FFFFFF",
                border: "1px soild #b5cBEB",
                display: 'grid',
                gridTemplateColumns: '1fr 0fr',
                alignItems: 'end',
                gap: '10px'
            },
            removeOption: {
                // ...withPixelLineHeight(theme.typography.bodyShortRegular),
                background: "none",
                border: "none",
                color: "#E04757",
                paddingLeft: 0,
                marginTop: '4px',
                marginBottom: '10px',
                padding: '0px'
            },
            addButtonContainer: {
                display: "flex",
                background: '#4b6790',
                borderRadius: '8px',
                width: '100px'
            },
            footer: {
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-end",
                padding: "24px",
                width: "100%",
                boxSizing: "border-box",
                gap: '10px',
                paddingTop: '10px',
                paddingBottom: '10px',
                flexShrink: 0, // Prevent footer from shrinking
            },
            footterChild: {
                display: "flex",
            },
            buttonMargin: {
                marginRight: 16,
            },
            pollTypeContainer: {
                marginBottom: "16px",
                padding: "12px",
                backgroundColor: "#1f1f1f",
                borderRadius: "8px",
            },
            pollTypeLabel: {
                color: interfaceConfig.applyMeetingSettings ? (_a = getBrandingColorPalette(interfaceConfig)) === null || _a === void 0 ? void 0 : _a.accent1 : '#fff',
                fontSize: "14px",
                fontWeight: 600,
                marginBottom: "8px",
            },
            pollTypeOptions: {
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
            },
        };
    });
    const { classes } = useStyles(interfaceConfig);
    const registerFieldRef = useCallback((i, r) => {
        if (r === null) {
            return;
        }
        answerInputs.current[i] = r;
    }, [answerInputs]);
    useEffect(() => {
        answerInputs.current = answerInputs.current.slice(0, answers.length);
    }, [answers]);
    /*
     * This state allows us to requestFocus asynchronously, without having to worry
     * about whether a newly created input field has been rendered yet or not.
     */
    const [lastFocus, requestFocus] = useState(null);
    const conferenceView = useSelector((state) => state['features/base/conference'].conferenceView);
    useEffect(() => {
        if (lastFocus === null) {
            return;
        }
        const input = answerInputs.current[lastFocus];
        if (input === undefined) {
            return;
        }
        // input?.focus();
    }, [lastFocus]);
    const checkModifiers = useCallback((ev) => {
        // Composition events used to add accents to characters
        // despite their absence from standard US keyboards,
        // to build up logograms of many Asian languages
        // from their base components or categories and so on.
        if (ev.isComposing || ev.keyCode === 229) {
            // keyCode 229 means that user pressed some button,
            // but input method is still processing that.
            // This is a standard behavior for some input methods
            // like entering japanese or сhinese hieroglyphs.
            return true;
        }
        // Because this isn't done automatically on MacOS
        if (ev.key === "Enter" && ev.metaKey) {
            ev.preventDefault();
            onSubmit();
            return;
        }
        if (ev.ctrlKey || ev.metaKey || ev.altKey || ev.shiftKey) {
            return;
        }
    }, []);
    const onQuestionKeyDown = useCallback((ev) => {
        if (checkModifiers(ev)) {
            return;
        }
        if (ev.key === "Enter") {
            requestFocus(0);
            ev.preventDefault();
        }
    }, []);
    // Called on keypress in answer fields
    const onAnswerKeyDown = useCallback((i, ev) => {
        if (checkModifiers(ev)) {
            return;
        }
        if (ev.key === "Enter") {
            // We add a new option input
            // only if we are on the last option input
            if (i === answers.length - 1) {
                addAnswer(i + 1);
            }
            requestFocus(i + 1);
            ev.preventDefault();
        }
        else if (ev.key === "Backspace" &&
            ev.target.value === "" &&
            answers.length > 1) {
            removeAnswer(i);
            requestFocus(i > 0 ? i - 1 : 0);
            ev.preventDefault();
        }
        else if (ev.key === "ArrowDown") {
            if (i === answers.length - 1) {
                addAnswer();
            }
            requestFocus(i + 1);
            ev.preventDefault();
        }
        else if (ev.key === "ArrowUp") {
            if (i === 0) {
                addAnswer(0);
                requestFocus(0);
            }
            else {
                requestFocus(i - 1);
            }
            ev.preventDefault();
        }
    }, [answers, addAnswer, removeAnswer, requestFocus]);
    const pollsTypeContainerStyle = {
        marginBottom: "16px",
        padding: "12px",
        borderRadius: "8px",
        backgroundColor: (_a = getBrandingColorPalette(interfaceConfig)) === null || _a === void 0 ? void 0 : _a.primary
    };
    return (React.createElement("form", { className: classes.container, onSubmit: onSubmit, style: { maxHeight: conferenceView == 'fixed' ? 'calc(100vh - 250px)' : 'calc(100vh - 175px)' } },
        React.createElement("div", { className: classes.createContainer },
            React.createElement("div", { className: classes.header }, t("polls.create.create")),
            React.createElement("div", { className: classes.questionContainer },
                React.createElement(Input, { autoFocus: true, id: "polls-create-input", label: t("polls.create.pollQuestion"), maxLength: CHAR_LIMIT, onChange: setQuestion, onKeyPress: onQuestionKeyDown, placeholder: t("polls.create.questionPlaceholder"), textarea: true, value: question })),
            React.createElement("div", { style: pollsTypeContainerStyle },
                React.createElement("div", { className: classes.pollTypeLabel }, t("polls.create.pollType")),
                React.createElement("div", { className: classes.pollTypeOptions },
                    React.createElement(RadioButton, { checked: pollType === POLL_TYPE.SINGLE_SELECT, label: t("polls.create.singleSelect"), name: "pollType", interfaceConfig: interfaceConfig, tooltipContent: t("polls.create.singleSelectTooltip"), tooltipposition: "top-centre", onChange: () => setPollType(POLL_TYPE.SINGLE_SELECT), value: POLL_TYPE.SINGLE_SELECT }),
                    React.createElement(RadioButton, { checked: pollType === POLL_TYPE.MULTI_SELECT, label: t("polls.create.multiSelect"), name: "pollType", interfaceConfig: interfaceConfig, tooltipContent: t("polls.create.multipleSelectTooltip"), tooltipposition: "top-left", onChange: () => setPollType(POLL_TYPE.MULTI_SELECT), value: POLL_TYPE.MULTI_SELECT }))),
            React.createElement("ol", { className: classes.answerList },
                answers.map((answer, i) => (React.createElement("li", { className: classes.answer, key: i },
                    React.createElement(Input, { id: `polls-answer-input-${i}`, label: t("polls.create.pollOption", {
                            index: i + 1,
                        }), maxLength: CHAR_LIMIT, onChange: (val) => setAnswer(i, val), onKeyPress: (ev) => onAnswerKeyDown(i, ev), placeholder: t("polls.create.answerPlaceholder", { index: i + 1 }), ref: (r) => registerFieldRef(i, r), textarea: true, value: answer }),
                    answers.length > 2 && (React.createElement("button", { className: classes.removeOption, onClick: () => removeAnswer(i), type: "button" },
                        React.createElement(Icon, { fillColor: '#dc3c4e', src: IconCancelSelection })))))),
                React.createElement("div", { className: classes.addButtonContainer },
                    React.createElement(Button, { accessibilityLabel: t("polls.create.addOption"), disabled: answers.length >= ANSWERS_LIMIT, fullWidth: false, labelKey: "polls.create.addOption", onClick: () => {
                            addAnswer();
                            requestFocus(answers.length);
                        }, type: "polls.create.send" })))),
        React.createElement("div", { className: classes.footer },
            React.createElement("div", { className: classes.footterChild },
                React.createElement(Button, { accessibilityLabel: t("polls.create.cancel"), className: classes.buttonMargin, labelKey: "polls.create.cancel", onClick: () => setCreateMode(false), fullWidth: true, type: BUTTON_TYPES.SECONDARY }),
                React.createElement(Button, { accessibilityLabel: t("polls.create.send"), disabled: isSubmitDisabled, isSubmit: true, fullWidth: true, labelKey: "polls.create.send" })))));
};
/*
 * We apply AbstractPollCreate to fill in the AbstractProps common
 * to both the web and native implementations.
 */
export default AbstractPollCreate(PollCreate);
